import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Typography,
  Box,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useGameDispatch } from "../context/GameContext";
import type { BracketSize, GameType, GameMode, SongData } from "../types";
import { getArtists, getArtistSongCount, getSongsByArtist, getRandomSongs } from "../api/getCommands";
import SeoHead from "../components/SeoHead";
import { useTranslation } from "react-i18next";

interface ArtistOption {
  Id: number;
  Artist: string;
  ProfilePicture: string;
  Genre: string;
}

export default function GameSetupPage() {
  const { mode } = useParams<{ mode: string }>();
  const navigate = useNavigate();
  const dispatch = useGameDispatch();

  const [artists, setArtists] = useState<ArtistOption[]>([]);
  const [artist, setArtist] = useState<ArtistOption | null>(null);
  const [artist2, setArtist2] = useState<ArtistOption | null>(null);
  const [bracketSize, setBracketSize] = useState<BracketSize>(8);
  const [gameType, setGameType] = useState<GameType>("SingleE");
  const [songCount, setSongCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { t } = useTranslation();

  const isArtist = mode === "artist";
  const isVersus = mode === "versus";
  const isCustom = mode === "custom";

  // Fetch artists on mount
  useEffect(() => {
    getArtists()
      .then((data) => setArtists(data))
      .catch(() => setError(t("setup.failedToLoadArtists")));
  }, []);

  // Fetch song count when artist changes
  useEffect(() => {
    if (!artist) {
      setSongCount(null);
      return;
    }
    getArtistSongCount(artist.Id)
      .then((count) => setSongCount(count))
      .catch(() => setSongCount(null));
  }, [artist]);

  async function handleStart() {
    setLoading(true);
    setError("");

    let gameMode: GameMode = "ArtistBrackets";
    if (isCustom) gameMode = "customBrackets";
    if (isVersus) gameMode = "ArtistvsArtist";

    try {
      let songs: SongData[];

      if (isCustom) {
        songs = await getRandomSongs(bracketSize);
      } else if (isVersus && artist && artist2) {
        const half = Math.floor(bracketSize / 2);
        const [songs1, songs2] = await Promise.all([
          getSongsByArtist(artist.Id, half),
          getSongsByArtist(artist2.Id, half),
        ]);
        songs = [...songs1, ...songs2];
      } else if (artist) {
        songs = await getSongsByArtist(artist.Id, bracketSize);
      } else {
        setError(t("setup.selectArtist"));
        setLoading(false);
        return;
      }

      if (songs.length < bracketSize) {
        setError(
          t("setup.notEnoughSongs", { found: songs.length, need: bracketSize })
        );
        setLoading(false);
        return;
      }

      // Map API response to SongData format
      const mappedSongs: SongData[] = songs.slice(0, bracketSize).map((s: any) => ({
        URL: s.URL || "",
        ArtistId: String(s.ArtistId || ""),
        AlbumId: String(s.AlbumId || ""),
        Id: String(s.Id),
        Features: s.Features || "None",
        AlternativeVersion: s.AlternativeVersion || "",
        Artist: s.ArtistName || s.Artist || "",
        Genre: s.Genre || "",
        SongName: s.SongName || "",
        YearReleased: String(s.YearReleased || s.Year || ""),
        Year: String(s.Year || s.YearReleased || ""),
        Album: s.Album || s.AlbumName || "",
        AlbumName: s.AlbumName || s.Album || "",
        VideoViews: String(s.VideoViews || "0"),
        Genius: s.Genius || "",
        Spotify: "",
        AlbumArt: s.AlbumArt || "",
        isWorkingVideo: s.isWorkingVideo || "Yes",
      }));

      dispatch({
        type: "START_GAME",
        payload: {
          gameMode,
          gameType,
          bracketSize,
          songs: mappedSongs,
          artistSelected: artist?.Artist ?? "",
        },
      });
      navigate("/game/play");
    } catch {
      setError(t("setup.failedToLoad"));
    } finally {
      setLoading(false);
    }
  }

  const title = isArtist
    ? t("nav.artistBrackets")
    : isCustom
      ? t("nav.customGames")
      : t("home.artistVsArtist");

  const canStart =
    isCustom || (artist && (!isVersus || artist2)) ? !loading : false;

  // Determine which bracket sizes are available based on song count
  const maxBracket = songCount ?? 32;

  return (
    <Box id="setup-page" sx={{ maxWidth: 600, mx: "auto", mt: 4 }}>
      <SeoHead
        title={title}
        description={`Set up a ${title} bracket game. Choose your bracket size and elimination type.`}
        canonical={`/game/${mode}`}
      />
      <Typography id="setup-title" variant="h4" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isArtist
          ? "Search for any artist from our database to build a bracket from their songs. Pick a bracket size and elimination type, then start ranking your favorites in head-to-head matchups."
          : isVersus
            ? "Choose two artists and we'll combine their songs into one bracket. Half the songs come from each artist, creating head-to-head matchups both within and across their catalogs."
            : "Custom games pull a random mix of songs from across all artists and genres in our database. It's a great way to discover new music and compare songs you'd never normally put side by side."}
      </Typography>

      {error && (
        <Alert id="setup-error" severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper id="setup-form" sx={{ p: 3, mt: 3 }}>
        {(isArtist || isVersus) && (
          <Autocomplete
            options={artists}
            getOptionLabel={(opt) => opt.Artist}
            value={artist}
            onChange={(_, val) => setArtist(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={isVersus ? t("setup.artist1") : t("setup.searchArtist")}
                fullWidth
                sx={{ mb: 3 }}
              />
            )}
          />
        )}

        {isVersus && (
          <Autocomplete
            options={artists}
            getOptionLabel={(opt) => opt.Artist}
            value={artist2}
            onChange={(_, val) => setArtist2(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("setup.artist2")}
                fullWidth
                sx={{ mb: 3 }}
              />
            )}
          />
        )}

        {artist && !isCustom && songCount !== null && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("setup.songCount", { artist: artist.Artist, count: songCount })}
          </Typography>
        )}

        <FormControl id="setup-bracket-size" fullWidth sx={{ mb: 3 }}>
          <InputLabel>{t("setup.bracketSize")}</InputLabel>
          <Select
            value={bracketSize}
            label={t("setup.bracketSize")}
            onChange={(e) => setBracketSize(e.target.value as BracketSize)}
          >
            <MenuItem value={8}>8 {t("setup.songs")}</MenuItem>
            <MenuItem value={16} disabled={!isCustom && maxBracket < 16}>
              16 {t("setup.songs")}
            </MenuItem>
            <MenuItem value={32} disabled={!isCustom && maxBracket < 32}>
              32 {t("setup.songs")}
            </MenuItem>
            <MenuItem
              value={64}
              disabled={!isCustom && maxBracket < 64}
            >
              64 {t("setup.songs")}
            </MenuItem>
          </Select>
        </FormControl>

        <Box id="setup-elimination-type" sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("setup.eliminationType")}
          </Typography>
          <ToggleButtonGroup
            value={gameType}
            exclusive
            onChange={(_, val) => {
              if (!val) return;
              setGameType(val);
            }}
            fullWidth
          >
            <ToggleButton value="SingleE">{t("setup.singleElimination")}</ToggleButton>
            <ToggleButton value="DoubleE">{t("setup.doubleElimination")}</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Button
          id="setup-start-btn"
          variant="contained"
          size="large"
          fullWidth
          onClick={handleStart}
          disabled={!canStart}
          sx={{ mt: 1 }}
        >
          {loading ? <CircularProgress size={24} /> : t("setup.startGame")}
        </Button>
      </Paper>
    </Box>
  );
}
